import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import moment from "moment";
import { t } from "i18next";
import { StatusCode } from "api/protocols";
import { GetRegisters } from "api/rpc/2024-04/facilityAdmin/facility/register/register";
import {
  GetRegisterInventoryTransfer,
  TRegisterInventoryTransfer,
} from "api/rpc/2024-04/facilityAdmin/product/registerInventory";
import { Badge } from "components/badge/Badge";
import DataTable from "components/dataTable/DataTable";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";

type TRegisterInventoryTransferRow = TRegisterInventoryTransfer & {
  register_title?: string;
};

export default function RegisterInventoryTransferTable(props: {
  variantId?: number;
  unit?: string;
}) {
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<TRegisterInventoryTransferRow[] | undefined>(undefined);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTransfers(source.token);
    return () => source.cancel();
  }, [props.variantId]);

  async function loadTransfers(token?: CancelToken) {
    if (!props.variantId) {
      setRows([]);
      return;
    }

    setRows(undefined);

    const registersRes = await GetRegisters({}, false, token);
    if (token?.reason) {
      return;
    }
    if (registersRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting register inventory transfers")));
      setRows([]);
      return;
    }

    const registers = (registersRes.data ?? []).map(register => ({
      id: register.id,
      title: String(register.title ?? ""),
    }));
    if (registers.length === 0) {
      setRows([]);
      return;
    }

    const transferResponses = await Promise.all(
      registers.map(register =>
        GetRegisterInventoryTransfer(
          { register_id: register.id, variant_id: props.variantId },
          false,
          token,
        ),
      ),
    );
    if (token?.reason) {
      return;
    }

    if (transferResponses.some(response => response.status !== StatusCode.OK)) {
      dispatch(showError(t("Error getting register inventory transfers")));
    }

    const transferRows: TRegisterInventoryTransferRow[] = transferResponses.flatMap((response, index) => {
      if (response.status !== StatusCode.OK) {
        return [];
      }

      const transfers: TRegisterInventoryTransfer[] = response.data ?? [];
      return transfers.map(transfer => ({
        ...transfer,
        register_title: registers[index]?.title,
      }));
    });
    transferRows.sort((a, b) => b.id - a.id);

    setRows(transferRows);
  }

  return (
    <DataTable
      columns={[
        { label: t("Date") },
        { label: t("Register") },
        { label: t("") },
        { label: t("Quantity") },
        { label: t("Previous Quantity") },
        { label: t("New Quantity") },
      ]}
      loading={rows === undefined}
    >
      {rows?.map(row => (
        <tr key={row.id}>
          <td>{moment(row.created_at).format("MMM DD, YYYY h:mm A")}</td>
          <td>{row.register_title || `#${row.register_id}`}</td>
          <td>
            <Badge type={row.direction === "to_register" ? "success" : "lightblue"}>
              {row.direction === "to_register" ? t("Transfer to Register") : t("Return to Facility")}
            </Badge>
          </td>
          <td>
            {row.quantity}
            {props.unit ? ` ${props.unit}` : ""}
          </td>
          <td>{row.previous_quantity ?? ""}</td>
          <td>{row.new_quantity ?? ""}</td>
        </tr>
      ))}
      {rows?.length < 1 && (
        <tr>
          <td colSpan={6} align="center">{t("No register inventory transfers")}</td>
        </tr>
      )}
    </DataTable>
  );
}
